import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { translateLyrics, generateVocabulary } from '../lib/openai';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { FaPlay, FaPause, FaLanguage, FaBook, FaBackward, FaForward, FaChevronDown, FaGlobe, FaExpand, FaCompress } from 'react-icons/fa';

interface SongPlayerProps {
  song: {
    id: string;
    song_url: string;
    song_cover: string;
    creator: string;
    language: 'english' | 'spanish' | 'french';
    song_lyrics: string;
    translation: string | null;
    song_vocabulary: any;
  };
  onClose: () => void;
  autoPlay?: boolean;
}

interface LyricLine {
  text: string;
  translation?: string;
}

const SongPlayer: React.FC<SongPlayerProps> = ({ song, onClose, autoPlay = false }) => {
  const [playing, setPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentLyrics, setCurrentLyrics] = useState<LyricLine[]>([]);
  const [activeLyricIndex, setActiveLyricIndex] = useState(-1);
  const [showTranslation, setShowTranslation] = useState(true);
  const [showVocabulary, setShowVocabulary] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState<'english' | 'french' | 'haitian creole'>('english');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);
  const lyricsContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lines = song.song_lyrics.split('\n').filter(line => line.trim());
    const formattedLines = lines.map(line => ({
      text: line,
      translation: undefined
    }));
    setCurrentLyrics(formattedLines);

    // Add click outside listener for dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [song]);

  // Clear all translations when target language changes
  useEffect(() => {
    setCurrentLyrics(lyrics => 
      lyrics.map(line => ({
        ...line,
        translation: undefined
      }))
    );
  }, [targetLanguage]);

  const handleTranslate = async (text: string, index: number) => {
    if (translating) return;
    setTranslating(true);

    try {
      const translation = await translateLyrics(text, targetLanguage);
      if (translation) {
        setCurrentLyrics(lyrics => 
          lyrics.map((line, i) => 
            i === index ? { ...line, translation } : line
          )
        );
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate lyrics');
    } finally {
      setTranslating(false);
    }
  };

  const handleTranslateAll = async () => {
    if (translating) return;
    setTranslating(true);

    try {
      const translations = await Promise.all(
        currentLyrics.map(async (line) => {
          const translation = await translateLyrics(line.text, targetLanguage);
          return { ...line, translation };
        })
      );
      setCurrentLyrics(translations);
      toast.success('All lyrics translated successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to translate all lyrics');
    } finally {
      setTranslating(false);
    }
  };

  const handleReady = () => {
    setReady(true);
    if (playerRef.current) {
      setDuration(playerRef.current.getDuration());
    }
  };

  const handlePlay = () => {
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }
  };

  const handleError = (error: any) => {
    console.error('Player error:', error);
    toast.error('Error playing audio');
    setPlaying(false);
  };

  const handleProgress = ({ playedSeconds }: { playedSeconds: number }) => {
    setProgress(playedSeconds);
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !duration) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    
    if (playerRef.current) {
      playerRef.current.seekTo(newTime);
    }
    setProgress(newTime);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSkip = (seconds: number) => {
    if (!playerRef.current) return;
    
    const currentTime = playerRef.current.getCurrentTime();
    const newTime = Math.max(0, Math.min(currentTime + seconds, duration));
    playerRef.current.seekTo(newTime);
    setProgress(newTime);
  };

  return (
    <div className={`min-h-screen bg-gray-900 flex flex-col ${focusMode ? 'pt-0' : ''} relative`}>
      {/* Song Cover Section - Hidden in Focus Mode */}
      {!focusMode && (
        <div className="relative w-full" style={{ maxHeight: '40vh' }}>
          <img
            src={song.song_cover}
            alt={song.creator}
            className="w-full h-full object-cover"
            style={{ maxHeight: '40vh' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900 flex items-end justify-between p-4">
            <div className="text-white">
              <h1 className="text-2xl font-bold">{song.creator}</h1>
              <p className="text-sm opacity-80">{song.language}</p>
            </div>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
                  showTranslation 
                    ? 'bg-yellow-400 text-black' 
                    : 'bg-black/50 text-yellow-400 backdrop-blur-lg'
                } transition-colors duration-200`}
              >
                <FaGlobe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {targetLanguage.charAt(0).toUpperCase() + targetLanguage.slice(1)}
                </span>
                <FaChevronDown className="w-3 h-3" />
              </button>

              {showLanguageDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-black/90 backdrop-blur-lg rounded-lg shadow-lg py-1 z-50">
                  <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-800">
                    Translate to:
                  </div>
                  {['english', 'french', 'haitian creole'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setTargetLanguage(lang as 'english' | 'french' | 'haitian creole');
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm ${
                        targetLanguage === lang
                          ? 'text-yellow-400 bg-gray-800'
                          : 'text-white hover:bg-gray-800'
                      } transition-colors duration-200`}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                  <div className="border-t border-gray-800 mt-1">
                    <button
                      onClick={() => {
                        handleTranslateAll();
                        setShowLanguageDropdown(false);
                      }}
                      disabled={translating}
                      className="w-full px-4 py-2 text-left text-sm text-yellow-400 hover:bg-gray-800 disabled:opacity-50 transition-colors duration-200"
                    >
                      {translating ? 'Translating...' : 'Translate All'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lyrics Container */}
      <div className={`flex-1 overflow-hidden px-4 ${focusMode ? 'bg-black' : ''}`}>
        <div 
          ref={lyricsContainerRef}
          className="h-full overflow-y-auto py-6 space-y-4 lyrics-container pb-20 md:pb-6"
          style={{ 
            height: focusMode ? '100vh' : 'calc(100vh - 40vh - 120px)',
            scrollBehavior: 'smooth',
            paddingBottom: window.innerWidth < 768 ? '100px' : '80px'
          }}
        >
          {currentLyrics.map((line, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg transition-all duration-300 ${
                index === activeLyricIndex
                  ? 'bg-black scale-105'
                  : 'hover:bg-gray-800'
              }`}
            >
              <p className={`text-lg md:text-xl ${
                index === activeLyricIndex ? 'text-yellow-400 font-medium' : 'text-white'
              }`}>
                {line.text}
              </p>
              {showTranslation && (
                line.translation ? (
                  <p className="text-sm text-yellow-200 mt-1">{line.translation}</p>
                ) : (
                  <button
                    onClick={() => handleTranslate(line.text, index)}
                    disabled={translating}
                    className="mt-2 text-sm text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                  >
                    {translating ? 'Translating...' : `Translate to ${targetLanguage}`}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Separator Line */}
      <div className="hidden md:block fixed bottom-[55px] left-0 right-0 h-[1px] bg-yellow-400 opacity-30"></div>

      {/* Fixed Audio Controls - Hidden on Mobile */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 bg-black bg-opacity-90 backdrop-blur-lg p-2 border-t border-gray-800">
        {/* Progress Bar */}
        <div 
          ref={progressBarRef}
          className="relative h-1 bg-gray-800 rounded-full overflow-hidden cursor-pointer mb-2"
          onClick={handleProgressBarClick}
        >
          <div 
            className="absolute left-0 top-0 h-full bg-yellow-400 transition-all duration-100"
            style={{ width: `${(progress / duration) * 100}%` }}
          />
        </div>

        {/* Time and Controls */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-200 min-w-[3rem]">
            {formatTime(progress)}
          </span>
          
          <div className="flex items-center justify-center space-x-8">
            <button
              onClick={() => handleSkip(-10)}
              className="text-gray-400 hover:text-white"
            >
              <FaBackward className="w-4 h-4" />
            </button>

            <button
              onClick={() => setPlaying(!playing)}
              disabled={!ready}
              className="text-white hover:text-yellow-400"
            >
              {playing ? (
                <FaPause className="w-6 h-6" /> 
              ) : (
                <FaPlay className="w-6 h-6" />
              )}
            </button>

            <button
              onClick={() => handleSkip(10)}
              className="text-gray-400 hover:text-white"
            >
              <FaForward className="w-4 h-4" />
            </button>
          </div>

          <span className="text-xs text-gray-400 min-w-[3rem] text-right">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Mobile Floating Play Button */}
      <div className="md:hidden fixed bottom-20 right-6 z-50">
        <button
          onClick={() => setPlaying(!playing)}
          disabled={!ready}
          className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 transform hover:scale-110 active:scale-95"
        >
          {playing ? (
            <FaPause className="w-6 h-6 text-black ml-0.5" />
          ) : (
            <FaPlay className="w-6 h-6 text-black ml-1" />
          )}
        </button>
        
      </div>

      {/* Floating Controls */}
      <div className="fixed top-2 right-2 flex space-x-1">
        <button
          onClick={() => setFocusMode(!focusMode)}
          className={`p-3 rounded-full ${
            focusMode ? 'bg-yellow-400 text-black' : 'bg-black text-yellow-400'
          }`}
          title={focusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'}
        >
          {focusMode ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-3 h-3" />}
        </button>

        <button
          onClick={() => setShowVocabulary(!showVocabulary)}
          className={`p-2 rounded-full ${
            showVocabulary ? 'bg-yellow-400 text-black' : 'bg-black text-yellow-400'
          }`}
        >
          <FaBook className="w-3 h-3" />
        </button>

        <button
          onClick={onClose}
          className="p-1 rounded-full bg-black text-yellow-400"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Single Hidden Audio Player for All Devices */}
      <div className="hidden">
        <ReactPlayer
          ref={playerRef}
          url={song.song_url}
          playing={playing}
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={handleError}
          onProgress={handleProgress}
          onDuration={setDuration}
          progressInterval={100}
          config={{ file: { forceAudio: true } }}
        />
      </div>
    </div>
  );
};

export default SongPlayer;